/**
 * TemplatePicker — Modal for selecting an output format template and downloading.
 * Opened from the Accounting entries page when user clicks "Download".
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext';
import { outputFormatsService } from '../../services/api/outputFormatsService';
import type { SourceType, GenerateRequest, OutputFormatTemplateSummary } from '../../types/outputFormats';
import './OutputFormats.css';

interface TemplatePickerProps {
  clientId: string;
  sourceType: SourceType;
  params: GenerateRequest['params'];
  onClose: () => void;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
}

const LAST_USED_KEY_PREFIX = 'outputFormat:lastUsed';

const TemplatePicker: React.FC<TemplatePickerProps> = ({
  clientId,
  sourceType,
  params,
  onClose,
  onSuccess,
  onError,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [templates, setTemplates] = useState<OutputFormatTemplateSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

  const localStorageKey = `${LAST_USED_KEY_PREFIX}:${user?.id}:${sourceType}`;

  // Trigger file download from blob
  const triggerDownload = useCallback((blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }, []);

  // Generate and download
  const doDownload = useCallback(async (templateId: string | null) => {
    setDownloading(true);
    try {
      const { blob, filename } = await outputFormatsService.generate(clientId, {
        templateId,
        sourceType,
        params,
      });
      triggerDownload(blob, filename);
      onSuccess?.(t('outputFormats.picker.downloadSuccess', 'File downloaded successfully'));
      onClose();
    } catch (err) {
      onError?.(
        err instanceof Error
          ? err.message
          : t('outputFormats.picker.downloadFailed', 'Download failed — please try again')
      );
    } finally {
      setDownloading(false);
    }
  }, [clientId, sourceType, params, triggerDownload, onSuccess, onError, onClose, t]);

  // Load templates on mount
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const all = await outputFormatsService.listTemplates(clientId, sourceType);
        const active = all.filter((tpl) => tpl.isActive);

        if (cancelled) return;

        if (active.length === 0) {
          // No templates — download directly with default format
          doDownload(null);
          return;
        }

        // Sort alphabetically
        active.sort((a, b) => a.name.localeCompare(b.name));
        setTemplates(active);

        // Determine pre-selection
        const lastUsed = localStorage.getItem(localStorageKey);
        if (lastUsed && active.some((tpl) => tpl.templateId === lastUsed)) {
          setSelectedTemplateId(lastUsed);
        } else {
          const defaultTpl = active.find((tpl) => tpl.isDefault);
          setSelectedTemplateId(defaultTpl ? defaultTpl.templateId : null);
        }
      } catch (err) {
        if (!cancelled) {
          onError?.(
            err instanceof Error
              ? err.message
              : t('outputFormats.picker.downloadFailed', 'Download failed — please try again')
          );
          onClose();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [clientId, sourceType, localStorageKey, doDownload, onError, onClose, t]);

  const handleDownload = () => {
    // Persist selection
    if (selectedTemplateId) {
      localStorage.setItem(localStorageKey, selectedTemplateId);
    } else {
      localStorage.removeItem(localStorageKey);
    }
    doDownload(selectedTemplateId);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !downloading) {
      onClose();
    }
  };

  // Show nothing while loading or if no templates (direct download)
  if (loading) return null;
  if (templates.length === 0) return null;

  return (
    <div className="modal-overlay" onClick={handleBackdropClick}>
      <div className="modal-content of-picker-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('outputFormats.picker.title', 'Download Accounting Entries')}</h2>
          <button className="close-button" onClick={onClose} disabled={downloading}>×</button>
        </div>

        <div className="modal-body of-picker-body">
          {/* Default option */}
          <label className={`of-picker-option${selectedTemplateId === null ? ' of-picker-selected' : ''}`}>
            <input
              type="radio"
              name="templatePicker"
              checked={selectedTemplateId === null}
              onChange={() => setSelectedTemplateId(null)}
              disabled={downloading}
            />
            <div className="of-picker-option-info">
              <div className="of-picker-option-name">
                {t('outputFormats.picker.defaultOption', 'Default (No Template)')}
                <span className="format-badge format-csv">CSV</span>
              </div>
              <div className="of-picker-option-desc">
                {t('outputFormats.picker.defaultDescription', 'Exports all columns in standard format')}
              </div>
            </div>
          </label>

          {/* Template options */}
          {templates.map((tpl) => (
            <label
              key={tpl.templateId}
              className={`of-picker-option${selectedTemplateId === tpl.templateId ? ' of-picker-selected' : ''}`}
            >
              <input
                type="radio"
                name="templatePicker"
                checked={selectedTemplateId === tpl.templateId}
                onChange={() => setSelectedTemplateId(tpl.templateId)}
                disabled={downloading}
              />
              <div className="of-picker-option-info">
                <div className="of-picker-option-name">
                  {tpl.isDefault && <span className="of-picker-star" title={t('outputFormats.tooltips.defaultTemplate')}>&#9733;</span>}
                  {tpl.name}
                  <span className={`format-badge format-${tpl.outputFormat.toLowerCase()}`}>
                    {tpl.outputFormat}
                  </span>
                </div>
                {tpl.description && (
                  <div className="of-picker-option-desc">{tpl.description}</div>
                )}
              </div>
            </label>
          ))}
        </div>

        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1rem' }}>
          <button className="btn-secondary" onClick={onClose} disabled={downloading}>
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            className="btn-primary"
            onClick={handleDownload}
            disabled={downloading}
            style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}
          >
            {downloading && <div className="spinner" style={{ width: 14, height: 14, borderWidth: 2, margin: 0 }} />}
            {downloading
              ? t('outputFormats.picker.downloading', 'Preparing download...')
              : t('outputFormats.picker.downloadButton', 'Download')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplatePicker;
